import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateWebsiteDestinationPageRequestSchema,
  destinationPagePublicPath,
  slugify,
  type CreateWebsiteDestinationPageRequest,
  type WebsitePublishStatus,
} from '@utazzo/shared';
import { UzButton, UzCombobox, UzForm, UzFormField, UzInput, UzTabs } from '@utazzo/ui';
import { ArrowLeft, Wand2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch, type Resolver } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { CategoryPagesMultiSelect } from '../components/CategoryPagesMultiSelect';
import { FaqsEditor } from '../components/FaqsEditor';
import { ReelsEditor } from '../components/ReelsEditor';
import { ReviewsEditor } from '../components/ReviewsEditor';
import { SeoLinksEditor } from '../components/SeoLinksEditor';
import { SlidersEditor } from '../components/SlidersEditor';
import {
  CREATE_DEFAULTS,
  firstErrorTab,
  TAB_LABEL,
  toForm,
  toPayload,
} from '../destination-page-form';
import {
  useCreateDestinationPage,
  useDestinationPageDetail,
  usePublishDestinationPage,
  useUnpublishDestinationPage,
  useUpdateDestinationPage,
} from '../use-destination-pages';

import { useCategoryPagesList } from '@/features/category-pages/use-category-pages';
import { useDestinationsList } from '@/features/destinations/use-destinations';
import { useSubmitForReview } from '@/features/website-approvals/use-website-approvals';
import { MediaDropField } from '@/features/website-cms/components/MediaDropField';
import { RichTextEditor } from '@/features/website-cms/components/RichTextEditor';
import { useListReturn } from '@/hooks/use-list-return';

const STATUS_META: Record<WebsitePublishStatus, { dot: string; label: string }> = {
  DRAFT: { dot: 'bg-zinc-400', label: 'Draft' },
  PENDING_REVIEW: { dot: 'bg-amber-500', label: 'In review' },
  PUBLISHED: { dot: 'bg-emerald-500', label: 'Published' },
  ARCHIVED: { dot: 'bg-rose-500', label: 'Archived' },
};

type Tab = 'details' | 'about' | 'sliders' | 'faq' | 'reels' | 'seoLinks' | 'reviews' | 'seo';

export const DestinationPageDetailPage = (): React.JSX.Element => {
  const { id } = useParams();
  const navigate = useNavigate();
  const backToList = useListReturn('/destination-pages');
  const isNew = id === 'new';

  const detailQuery = useDestinationPageDetail(isNew ? undefined : id);
  const page = detailQuery.data ?? null;

  const createPage = useCreateDestinationPage();
  const updatePage = useUpdateDestinationPage();
  const publishPage = usePublishDestinationPage();
  const unpublishPage = useUnpublishDestinationPage();
  const submitForReview = useSubmitForReview();

  const [activeTab, setActiveTab] = useState<Tab>('details');

  const form = useForm<CreateWebsiteDestinationPageRequest>({
    resolver: zodResolver(
      CreateWebsiteDestinationPageRequestSchema,
    ) as Resolver<CreateWebsiteDestinationPageRequest>,
    defaultValues: CREATE_DEFAULTS,
  });

  useEffect(() => {
    if (isNew) form.reset(CREATE_DEFAULTS);
    else if (page) form.reset(toForm(page));
  }, [isNew, page, form]);

  const destinationsQuery = useDestinationsList({
    page: 1,
    pageSize: 200,
    sortBy: 'name',
    sortDir: 'asc',
  });
  const destinationOptions = useMemo(
    () => [
      { value: '', label: '- None -' },
      ...(destinationsQuery.data?.data ?? []).map((d) => ({ value: d.id, label: d.name })),
    ],
    [destinationsQuery.data],
  );

  const categoriesQuery = useCategoryPagesList({
    page: 1,
    pageSize: 200,
    sortBy: 'title',
    sortDir: 'asc',
  });
  const watchedSlug = useWatch({ control: form.control, name: 'slug' });
  const watchedCategoryIds = useWatch({ control: form.control, name: 'categoryPageIds' }) ?? [];
  const publicPathHint = useMemo(() => {
    const pageSlug = watchedSlug?.trim();
    const categorySlug = watchedCategoryIds
      .map((id) => categoriesQuery.data?.data.find((p) => p.id === id)?.slug)
      .find(Boolean);
    if (!pageSlug) return 'Public URL: /{category}/{slug} - set slug and category pages';
    if (!categorySlug) return `Assign a category page to publish at /{category}/${pageSlug}`;
    return `Public URL: ${destinationPagePublicPath(categorySlug, pageSlug)}`;
  }, [watchedSlug, watchedCategoryIds, categoriesQuery.data]);

  const pending =
    createPage.isPending ||
    updatePage.isPending ||
    publishPage.isPending ||
    unpublishPage.isPending;
  const status = page ? STATUS_META[page.status] : null;

  const submit = form.handleSubmit(
    async (values) => {
      const payload = toPayload(values);
      if (isNew) {
        const created = await createPage.mutateAsync(payload);
        navigate(`/destination-pages/${created.id}`);
      } else if (page) {
        await updatePage.mutateAsync({ id: page.id, payload });
        form.reset(values);
      }
    },
    (errors) => {
      const tab = firstErrorTab(Object.keys(errors)) as Tab;
      setActiveTab(tab);
      toast.error(`Please fix the highlighted fields on the “${TAB_LABEL[tab]}” tab.`);
    },
  );

  const tabs = useMemo(
    () => [
      {
        value: 'details' as const,
        label: 'Details',
        content: (
          <div className="grid items-start gap-4 max-sm:grid-cols-1 lg:grid-cols-2">
            <UzFormField name="title">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string) ?? ''}
                  label="Title"
                  placeholder="Bali Tour Packages"
                  required
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="slug">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string) ?? ''}
                  label="Slug"
                  placeholder="bali-tour-packages"
                  hint={publicPathHint}
                  required
                  error={error}
                  disabled={pending}
                  rightAdornment={
                    <button
                      type="button"
                      title="Generate slug from title"
                      aria-label="Generate slug from title"
                      disabled={pending}
                      onClick={() => field.onChange(slugify(form.getValues('title') ?? ''))}
                      className="text-zinc-400 transition-colors hover:text-zinc-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Wand2 className="h-4 w-4" />
                    </button>
                  }
                />
              )}
            </UzFormField>
            <UzFormField name="destinationId">
              {({ field, error }) => (
                <UzCombobox
                  name={field.name}
                  label="Destination (master)"
                  placeholder="Select a destination…"
                  searchPlaceholder="Search destinations…"
                  value={(field.value as string | null) ?? ''}
                  onValueChange={(v) => field.onChange(v === '' ? null : v)}
                  options={destinationOptions}
                  loading={destinationsQuery.isLoading}
                  hint="Packages are matched to this destination."
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <CategoryPagesMultiSelect form={form} disabled={pending} />
            <UzFormField name="sortOrder">
              {({ field, error }) => (
                <UzInput
                  type="number"
                  name={field.name}
                  value={typeof field.value === 'number' ? String(field.value) : ''}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  label="Sort order"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="heroHeading">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null) ?? ''}
                  label="Hero heading"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="heroSubheading">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null) ?? ''}
                  label="Hero subheading"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="heroMediaId">
              {({ field }) => (
                <div className="space-y-1 lg:col-span-2">
                  <label className="text-sm font-medium text-zinc-700">Hero image</label>
                  <p className="text-xs text-zinc-500">
                    Fallback when no hero video is set, and poster while the video loads.
                  </p>
                  <MediaDropField
                    value={(field.value as string | null) ?? null}
                    kind="image"
                    folder="destination-pages"
                    disabled={pending}
                    onChange={(v) => field.onChange(v)}
                  />
                </div>
              )}
            </UzFormField>
            <UzFormField name="heroVideoId">
              {({ field }) => (
                <div className="space-y-1 lg:col-span-2">
                  <label className="text-sm font-medium text-zinc-700">Hero video</label>
                  <p className="text-xs text-zinc-500">Optional. Plays behind the hero when set.</p>
                  <MediaDropField
                    value={(field.value as string | null) ?? null}
                    kind="video"
                    folder="destination-pages"
                    disabled={pending}
                    onChange={(v) => field.onChange(v)}
                  />
                </div>
              )}
            </UzFormField>
            <UzFormField name="tileMediaId">
              {({ field }) => (
                <div className="space-y-1 lg:col-span-2">
                  <label className="text-sm font-medium text-zinc-700">Tile image</label>
                  <p className="text-xs text-zinc-500">
                    Shown for this destination on a category page tile. Falls back to the hero image
                    when empty.
                  </p>
                  <MediaDropField
                    value={(field.value as string | null) ?? null}
                    kind="image"
                    folder="destination-pages"
                    disabled={pending}
                    onChange={(v) => field.onChange(v)}
                  />
                </div>
              )}
            </UzFormField>
          </div>
        ),
      },
      {
        value: 'about' as const,
        label: 'About',
        content: (
          <div className="space-y-4">
            <UzFormField name="aboutHeading">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null) ?? ''}
                  label="About section heading"
                  placeholder="About Rajasthan Tour Packages"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="aboutHtml">
              {({ field }) => (
                <RichTextEditor
                  value={(field.value as string | null) ?? ''}
                  onChange={field.onChange}
                  label="About this destination"
                  folder="destination-pages"
                  disabled={pending}
                />
              )}
            </UzFormField>
          </div>
        ),
      },
      {
        value: 'sliders' as const,
        label: 'Sliders',
        content: <SlidersEditor form={form} disabled={pending} />,
      },
      {
        value: 'faq' as const,
        label: 'FAQ',
        content: <FaqsEditor form={form} disabled={pending} />,
      },
      {
        value: 'reels' as const,
        label: 'Reels',
        content: <ReelsEditor form={form} disabled={pending} />,
      },
      {
        value: 'seoLinks' as const,
        label: 'SEO links',
        content: <SeoLinksEditor form={form} disabled={pending} />,
      },
      {
        value: 'reviews' as const,
        label: 'Reviews',
        content: <ReviewsEditor form={form} disabled={pending} />,
      },
      {
        value: 'seo' as const,
        label: 'SEO',
        content: (
          <div className="max-w-2xl space-y-4">
            <UzFormField name="metaTitle">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null) ?? ''}
                  label="Meta title"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="metaDescription">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null) ?? ''}
                  label="Meta description"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="metaKeywords">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null) ?? ''}
                  label="Meta keywords"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="ogImageId">
              {({ field }) => (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-zinc-700">
                    Social share image (OG)
                  </label>
                  <MediaDropField
                    value={(field.value as string | null) ?? null}
                    kind="image"
                    folder="destination-pages"
                    disabled={pending}
                    onChange={(v) => field.onChange(v)}
                  />
                </div>
              )}
            </UzFormField>
          </div>
        ),
      },
    ],
    [form, pending, destinationOptions, destinationsQuery.isLoading, publicPathHint],
  );

  return (
    <div className="-mx-4 -mt-6 flex min-h-full flex-col lg:-mx-8 lg:-mt-8">
      <div className="sticky -top-6 z-30 flex h-12 items-center gap-3 border-b border-zinc-200 bg-white px-6 max-sm:h-auto max-sm:flex-wrap max-sm:gap-x-2 max-sm:gap-y-1.5 max-sm:px-4 max-sm:py-2 lg:-top-8 lg:px-8">
        <button
          type="button"
          onClick={backToList}
          className="-ml-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
          aria-label="Back"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 items-center gap-2 max-sm:max-w-[calc(100%-2.5rem)]">
          <span className="text-xs font-medium text-zinc-500 max-sm:shrink-0 max-sm:whitespace-nowrap">
            Destination pages
          </span>
          <span className="text-zinc-300">/</span>
          <span className="truncate text-sm font-semibold text-zinc-900">
            {isNew ? 'New page' : (page?.title ?? 'Page')}
          </span>
        </div>
        <span className="flex-1 max-sm:basis-full" />
        {status && (
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
            <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
            {status.label}
          </span>
        )}
        {/* Opt-in review: publish stays available to everyone who has it, and
            this is the "let the manager sign off first" alternative. */}
        {!isNew && page && page.status !== 'PENDING_REVIEW' && (
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => submitForReview.mutate({ type: 'DESTINATION_PAGE', id: page.id })}
            loading={submitForReview.isPending}
            disabled={pending}
          >
            Submit for review
          </UzButton>
        )}
        {!isNew && page && page.status !== 'PUBLISHED' && (
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => publishPage.mutate(page.id)}
            loading={publishPage.isPending}
            disabled={pending}
          >
            Publish
          </UzButton>
        )}
        {!isNew && page && page.status === 'PUBLISHED' && (
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => unpublishPage.mutate(page.id)}
            loading={unpublishPage.isPending}
            disabled={pending}
          >
            Unpublish
          </UzButton>
        )}
        <UzButton variant="ghost" size="sm" onClick={backToList} disabled={pending}>
          Cancel
        </UzButton>
        <UzButton size="sm" onClick={submit} loading={pending}>
          {isNew ? 'Create' : 'Save'}
        </UzButton>
      </div>
      <div className="px-6 py-6 lg:px-8">
        <UzForm form={form} onSubmit={submit}>
          <UzTabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as Tab)}
            tabs={tabs}
            // Phones: the strip scrolls sideways instead of running off-screen.
            listClassName="max-sm:max-w-full max-sm:justify-start max-sm:overflow-x-auto"
          />
        </UzForm>
      </div>
    </div>
  );
};
